import { useCallback, useMemo, useRef, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	useGetMessageFeedbackType,
	useSetMessageFeedbackSent,
} from '@atlassian/conversation-assistant-store/controllers/message-feedback';

import { useAutoDismiss } from '../../common/hooks/useAutoDismiss';
import { type FeedbackType } from '../../common/ui/message-footer-buttons/feedback-confirmation-banner/FeedbackType';
import { getCurrentOrigin } from '../../common/utils/date-time-util/getCurrentOrigin';
import { isAtlassianEmployee } from '../../common/utils/date-time-util/isAtlassianEmployee';
import { isInternalUserWithAutoIncludeHistory } from '../../common/utils/isInternalUserWithAutoIncludeHistory';
import { shouldShowOptInPrompt } from '../../common/utils/shouldShowOptInPrompt';
import { submitAUPFeedbackToJSM } from '../../services/feedback/aup-feedback';
import { type CsatRating, submitQuickFeedbackToJSM } from '../../services/feedback/jsm-feedback';
import { sendConvoAIFeedback } from '../../services/feedback/sendConvoAIFeedback';
import { type FeedbackTrigger } from '../../services/feedback/types';

export interface UseFeedbackV2Config {
	entryPointId: string;
	aaid: string;
	name?: string;
	email?: string;
	productKey: string;
	stagingAreaEnabled?: boolean;
	agentMessageId: string;
	messageRequestId?: string;
	conversationId?: string;
	selectedAgent?: {
		id?: string;
		named_id?: string;
		external_config_reference?: string;
	};
	history?: string | object;
	analyticsMessageActions?: string[];
	autoDismissDelay?: number;
	traceId?: string;
	isAUPError?: boolean;
	minionAlias?: string;
	storeUserMessages?: boolean;
	siteId?: string;
	experienceId?: string;
	productHeader?: string;
	channelId?: string;
	detailedFeedbackSource?: string;
	setStoreUserMessages?: (enabled: boolean) => void;
	interactionModeExecuted?: string;
	feedbackTrigger?: FeedbackTrigger;
}

export interface UseFeedbackV2Result {
	feedbackType: FeedbackType | null;
	isBannerVisible: boolean;
	shouldHideCheckboxes: boolean;
	handleThumbClick: (isLike: boolean) => void;
	handleCsatFeedback: (rating: CsatRating) => boolean;
	restartAutoDismiss: () => void;
	pauseAutoDismiss: () => void;
	csatRating: CsatRating | null;
	handleProvideAdditionalFeedback: () => void;
	handleFeedbackSubmit: (data: {
		categories: string[];
		additionalFeedback: string;
		includeMessages: boolean;
		enrollInResearch: boolean;
	}) => Promise<void>;
	handleDismissBanner: () => void;
	handleIncludeMessagesChecked: () => void;
	handleOptIn: () => void;
	isOptedIn: boolean;
	isAUPThumbsDownBannerVisible: boolean;
	showAUPThumbsDownBanner: () => void;
	hideAUPThumbsDownBanner: () => void;
	handleAUPIncorrectFlagSubmit: (data: {
		feedbackText: string;
		includeInput: boolean;
		enrollInResearch: boolean;
	}) => Promise<void>;
}

const DEFAULT_AUTO_DISMISS_DELAY = 10000;
const QUICK_FEEDBACK_AUTO_DISMISS_DELAY = 20000;
const EMPTY_FEEDBACK_TRIGGER_PROPS = {} as const;

export const useFeedbackV2 = (config: UseFeedbackV2Config): UseFeedbackV2Result => {
	const {
		entryPointId,
		aaid,
		name,
		email,
		productKey,
		stagingAreaEnabled,
		agentMessageId,
		messageRequestId,
		conversationId,
		selectedAgent,
		history,
		analyticsMessageActions,
		autoDismissDelay,
		traceId,
		isAUPError,
		minionAlias,
		storeUserMessages,
		siteId,
		experienceId,
		productHeader,
		setStoreUserMessages,
		detailedFeedbackSource,
		interactionModeExecuted,
		feedbackTrigger,
	} = config;
	const { sendAnalyticsEvent, trackAIMAUEvent } = useAnalytics();
	const interactionModeExecutedAttributes = useMemo(
		() => (interactionModeExecuted ? { interactionModeExecuted } : {}),
		[interactionModeExecuted],
	);

	const feedbackTriggerProps = useMemo(
		() => (feedbackTrigger ? { feedbackTrigger } : EMPTY_FEEDBACK_TRIGGER_PROPS),
		[feedbackTrigger],
	);

	const shouldAutoInclude = isInternalUserWithAutoIncludeHistory(email, storeUserMessages);

	const feedbackType = useGetMessageFeedbackType(agentMessageId, conversationId) ?? null;
	const setMessageFeedbackSent = useSetMessageFeedbackSent();
	const hasSubmittedDetailedFeedbackRef = useRef(false);
	const [isOptedIn, setIsOptedIn] = useState(false);

	const [csatRating, setCsatRating] = useState<CsatRating | null>(null);

	const submitQuickFeedback = useCallback(() => {
		if (isAUPError) {
			return;
		}
		if (!hasSubmittedDetailedFeedbackRef.current && feedbackType !== null) {
			const includeMessages = shouldAutoInclude;
			const enrollInResearch = shouldShowOptInPrompt(email);
			submitQuickFeedbackToJSM({
				entryPointId,
				metadata: {
					aaid: aaid ?? 'unknown',
					answerQuality: feedbackType === 'like' ? 'good' : 'bad',
					assistantResponseId: agentMessageId,
					messageRequestId,
					origin: getCurrentOrigin() ?? 'unknown',
					feedbackSource: `${productKey}-v2-quick-feedback`,
					...feedbackTriggerProps,
					selectedAgent: selectedAgent
						? {
								id: selectedAgent.id,
								externalConfigReference: selectedAgent.external_config_reference,
							}
						: undefined,
					conversationId,
					isDetailedFeedback: false,
					minionAlias,
					includeMessages,
					enrollInResearch,
					history: includeMessages ? history : undefined,
					csatRating: csatRating ?? undefined,
				},
				customerName: name || 'unknown',
				email,
			})
				.then(() => {
					sendAnalyticsEvent({
						action: 'submitted',
						actionSubject: 'quickFeedback',
						actionSubjectId: 'feedbackConfirmationBanner',
						attributes: {
							feedbackType: feedbackType === 'like' ? 'like' : 'dislike',
							messageId: agentMessageId,
							storeUserMessages: shouldAutoInclude,
							...feedbackTriggerProps,
						},
					});
				})
				.catch((error: Error) => {
					sendAnalyticsEvent({
						action: 'failed',
						actionSubject: 'feedbackSubmission',
						actionSubjectId: 'jsmQuickFeedback',
						attributes: {
							errorMessage: error?.message ?? 'Unknown error',
							feedbackType: feedbackType === 'like' ? 'like' : 'dislike',
							messageId: agentMessageId,
							...feedbackTriggerProps,
						},
					});
				});
		}
	}, [
		feedbackType,
		entryPointId,
		aaid,
		agentMessageId,
		messageRequestId,
		productKey,
		selectedAgent,
		conversationId,
		name,
		email,
		sendAnalyticsEvent,
		isAUPError,
		minionAlias,
		shouldAutoInclude,
		history,
		csatRating,
		feedbackTriggerProps,
	]);

	const {
		isVisible: isBannerVisible,
		showWithoutTimer: showBannerWithoutTimer,
		hide: hideBanner,
		cancelTimer: cancelAutoDismiss,
		restartTimer: restartAutoDismiss,
	} = useAutoDismiss({
		delay: autoDismissDelay ?? QUICK_FEEDBACK_AUTO_DISMISS_DELAY,
		onAutoDismiss: () => submitQuickFeedback(),
	});

	const {
		isVisible: isAUPThumbsDownBannerVisible,
		showWithoutTimer: showAUPThumbsDownBannerWithoutTimer,
		show: showAUPThumbsDownThankYou,
		hide: hideAUPThumbsDownBanner,
	} = useAutoDismiss({ delay: autoDismissDelay ?? DEFAULT_AUTO_DISMISS_DELAY });

	const handleThumbClick = useCallback(
		(isLike: boolean) => {
			setCsatRating(null);
			const newFeedbackType = isLike ? 'like' : 'dislike';

			setMessageFeedbackSent(agentMessageId, newFeedbackType, conversationId);
			hasSubmittedDetailedFeedbackRef.current = false;
			setIsOptedIn(false);
			showBannerWithoutTimer();
			restartAutoDismiss();

			sendAnalyticsEvent({
				eventType: 'track',
				action: 'clicked',
				actionSubject: 'thumbButton',
				attributes: {
					feedbackValue: isLike ? 'like' : 'dislike',
					conversationId,
					messageId: agentMessageId,
				},
			});

			trackAIMAUEvent({
				type: 'feedback',
				feedbackResult: isLike ? 'up' : 'down',
				proactive: false,
				messageId: agentMessageId,
				messageActions: analyticsMessageActions,
				singleInstrumentationID: agentMessageId,
				attributes: { ...interactionModeExecutedAttributes, ...feedbackTriggerProps },
			});

			if (
				conversationId &&
				siteId &&
				experienceId &&
				productHeader &&
				fg('service_agent_send_feedback_to_convo_ai_on_thumb')
			) {
				sendConvoAIFeedback({
					conversationId,
					messageId: agentMessageId,
					feedbackValue: isLike ? 'LIKE' : 'DISLIKE',
					productHeader,
					experienceId,
					channelId: config.channelId ?? 'rovo-chat',
					cloudId: siteId,
				}).catch(() => {
					// TODO RSA-569: send analytics event for feedback to Convo AI failed
				});
			}
		},
		[
			setMessageFeedbackSent,
			agentMessageId,
			conversationId,
			sendAnalyticsEvent,
			trackAIMAUEvent,
			analyticsMessageActions,
			siteId,
			experienceId,
			productHeader,
			showBannerWithoutTimer,
			restartAutoDismiss,
			config.channelId,
			interactionModeExecutedAttributes,
			feedbackTriggerProps,
		],
	);

	const handleCsatFeedback = useCallback(
		(rating: CsatRating): boolean => {
			setCsatRating(rating);

			const isLike = rating === 'completely' || rating === 'mostly';
			const newFeedbackType = isLike ? 'like' : 'dislike';

			setMessageFeedbackSent(agentMessageId, newFeedbackType, conversationId);
			hasSubmittedDetailedFeedbackRef.current = false;
			setIsOptedIn(false);
			showBannerWithoutTimer();
			restartAutoDismiss();

			return isLike;
		},
		[
			setMessageFeedbackSent,
			agentMessageId,
			conversationId,
			showBannerWithoutTimer,
			restartAutoDismiss,
		],
	);

	const handleProvideAdditionalFeedback = useCallback(() => {
		cancelAutoDismiss();
	}, [cancelAutoDismiss]);

	const handleFeedbackSubmit = useCallback(
		async (data: {
			categories: string[];
			additionalFeedback: string;
			includeMessages: boolean;
			enrollInResearch: boolean;
		}) => {
			cancelAutoDismiss();

			hasSubmittedDetailedFeedbackRef.current = true;

			const shouldIncludeHistory = shouldAutoInclude || data.includeMessages;
			const shouldEnrollInResearch = shouldShowOptInPrompt(email) || data.enrollInResearch;

			// Route "inappropriate" (harmful content) category to AUP endpoint
			if (data.categories.includes('inappropriate')) {
				try {
					await submitAUPFeedbackToJSM({
						metadata: {
							aaid: aaid ?? 'unknown',
							assistantResponseId: agentMessageId,
							messageRequestId,
							origin: getCurrentOrigin() ?? 'unknown',
							feedbackSource: `${productKey}-v2-harmful-content`,
							...feedbackTriggerProps,
							conversationId,
							history: shouldIncludeHistory ? history : undefined,
							isAupFeedback: true,
							minionAlias,
						},
						customerName: name || 'unknown',
						email,
						feedbackText: data.additionalFeedback,
						includeInput: shouldIncludeHistory,
						enrollInResearch: shouldEnrollInResearch,
					});

					trackAIMAUEvent({
						type: 'feedback',
						feedbackResult: 'down',
						proactive: false,
						messageId: agentMessageId,
						messageActions: analyticsMessageActions,
						singleInstrumentationID: agentMessageId,
						attributes: { ...interactionModeExecutedAttributes, ...feedbackTriggerProps },
					});

					sendAnalyticsEvent({
						action: 'submitted',
						actionSubject: 'aupFeedback',
						actionSubjectId: 'harmfulContentReport',
						attributes: {
							hasAdditionalFeedback: !!data.additionalFeedback,
							includeMessages: shouldIncludeHistory,
							enrollInResearch: shouldEnrollInResearch,
						},
					});

					restartAutoDismiss();
				} catch (error) {
					logException({
						name: 'harmfulContentSubmission',
						exception:
							error instanceof Error
								? error
								: new Error('Failed to submit harmful content feedback'),
						tags: {
							component: 'FeedbackConfirmationBanner',
							feedbackType: feedbackType ?? 'unknown',
							messageId: agentMessageId,
						},
					});

					sendAnalyticsEvent({
						action: 'failed',
						actionSubject: 'aupFeedback',
						actionSubjectId: 'harmfulContentReport',
						attributes: {
							errorMessage: error instanceof Error ? error.message : 'Unknown error',
							messageId: agentMessageId,
						},
					});

					restartAutoDismiss();
				}

				// Send AUP feedback to Convo AI as well — fire-and-forget
				if (
					conversationId &&
					siteId &&
					experienceId &&
					productHeader &&
					feedbackType &&
					fg('rovo_send_detailed_feedback_to_convo_ai')
				) {
					sendConvoAIFeedback({
						conversationId,
						messageId: agentMessageId,
						feedbackValue: feedbackType === 'like' ? 'LIKE' : 'DISLIKE',
						feedbackText: data.additionalFeedback || undefined,
						feedbackCategories: data.categories.length > 0 ? data.categories : undefined,
						productHeader,
						experienceId,
						channelId: config.channelId ?? 'rovo-chat',
						cloudId: siteId,
					}).catch(() => {
						// Non-blocking
					});
				}
				return;
			}

			try {
				const hasDetailedContent =
					data.categories.length > 0 ||
					!!data.additionalFeedback ||
					data.includeMessages ||
					data.enrollInResearch;

				await submitQuickFeedbackToJSM({
					entryPointId,
					metadata: {
						aaid: aaid ?? 'unknown',
						answerQuality: feedbackType === 'like' ? 'good' : 'bad',
						assistantResponseId: agentMessageId,
						messageRequestId,
						origin: getCurrentOrigin() ?? 'unknown',
						feedbackSource:
							detailedFeedbackSource ??
							(stagingAreaEnabled
								? `${productKey}-staging-v2-detailed`
								: `${productKey}-v2-detailed`),
						conversationId,
						selectedAgent: selectedAgent
							? {
									id: selectedAgent.id,
									externalConfigReference: selectedAgent.external_config_reference,
								}
							: undefined,
						categories: data.categories,
						additionalFeedback: data.additionalFeedback,
						includeMessages: shouldIncludeHistory,
						enrollInResearch: shouldEnrollInResearch,
						history: shouldIncludeHistory ? history : undefined,
						isDetailedFeedback: hasDetailedContent,
						minionAlias,
						csatRating: csatRating ?? undefined,
						...feedbackTriggerProps,
					},
					customerName: name || 'unknown',
					email,
				});

				trackAIMAUEvent({
					type: 'feedback',
					feedbackResult: feedbackType === 'like' ? 'up' : 'down',
					proactive: false,
					messageId: agentMessageId,
					messageActions: analyticsMessageActions,
					singleInstrumentationID: agentMessageId,
					attributes: { ...interactionModeExecutedAttributes, ...feedbackTriggerProps },
				});

				sendAnalyticsEvent({
					action: 'submitted',
					actionSubject: 'detailedFeedback',
					actionSubjectId: 'feedbackConfirmationBanner',
					attributes: {
						feedbackType,
						feedbackOrigin: csatRating ? 'csat' : 'thumb',
						categories: data.categories,
						categoriesCount: data.categories.length,
						hasAdditionalFeedback: !!data.additionalFeedback,
						includeMessages: shouldIncludeHistory,
						enrollInResearch: shouldEnrollInResearch,
						isInternalUser: isAtlassianEmployee(email),
						storeUserMessages: !!shouldAutoInclude,
						...feedbackTriggerProps,
					},
				});

				restartAutoDismiss();
			} catch (error) {
				logException({
					name: 'detailedFeedbackSubmission',
					exception:
						error instanceof Error ? error : new Error('Failed to submit detailed feedback'),
					tags: {
						component: 'FeedbackConfirmationBanner',
						feedbackType: feedbackType ?? 'unknown',
						messageId: agentMessageId,
						categoriesCount: String(data.categories.length),
						hasAdditionalFeedback: String(!!data.additionalFeedback),
						includeMessages: String(data.includeMessages),
						enrollInResearch: String(data.enrollInResearch),
					},
				});

				sendAnalyticsEvent({
					action: 'failed',
					actionSubject: 'detailedFeedback',
					actionSubjectId: 'feedbackConfirmationBanner',
					attributes: {
						errorMessage: error instanceof Error ? error.message : 'Unknown error',
						feedbackType,
						messageId: agentMessageId,
						...feedbackTriggerProps,
					},
				});

				restartAutoDismiss();
			}

			// Send detailed feedback (text + categories) to Convo AI — fire-and-forget
			if (
				conversationId &&
				siteId &&
				experienceId &&
				productHeader &&
				feedbackType &&
				fg('rovo_send_detailed_feedback_to_convo_ai')
			) {
				sendConvoAIFeedback({
					conversationId,
					messageId: agentMessageId,
					feedbackValue: feedbackType === 'like' ? 'LIKE' : 'DISLIKE',
					feedbackText: data.additionalFeedback || undefined,
					feedbackCategories: data.categories.length > 0 ? data.categories : undefined,
					productHeader,
					experienceId,
					channelId: config.channelId ?? 'rovo-chat',
					cloudId: siteId,
				}).catch(() => {
					// Non-blocking — don't fail the feedback submission if convo-ai call fails
				});
			}
		},
		[
			cancelAutoDismiss,
			restartAutoDismiss,
			entryPointId,
			aaid,
			name,
			email,
			feedbackType,
			agentMessageId,
			messageRequestId,
			productKey,
			stagingAreaEnabled,
			conversationId,
			selectedAgent,
			history,
			trackAIMAUEvent,
			analyticsMessageActions,
			sendAnalyticsEvent,
			minionAlias,
			shouldAutoInclude,
			interactionModeExecutedAttributes,
			feedbackTriggerProps,
			siteId,
			experienceId,
			productHeader,
			config.channelId,
			csatRating,
			detailedFeedbackSource,
		],
	);

	const handleDismissBanner = useCallback(() => {
		hideBanner();
		submitQuickFeedback();
	}, [hideBanner, submitQuickFeedback]);

	const handleAUPIncorrectFlagSubmit = useCallback(
		async (data: { feedbackText: string; includeInput: boolean; enrollInResearch: boolean }) => {
			showAUPThumbsDownThankYou();

			const shouldIncludeHistory = shouldAutoInclude || data.includeInput;
			const shouldEnrollInResearch = shouldAutoInclude || data.enrollInResearch;

			try {
				await submitAUPFeedbackToJSM({
					metadata: {
						aaid: aaid ?? 'unknown',
						assistantResponseId: agentMessageId,
						origin: getCurrentOrigin() ?? 'unknown',
						feedbackSource: `${productKey}-v2-incorrect-flag`,
						...feedbackTriggerProps,
						conversationId,
						history: shouldIncludeHistory ? history : undefined,
						traceId,
						isAupFeedback: true,
						minionAlias,
					},
					customerName: name || 'unknown',
					email,
					feedbackText: data.feedbackText,
					includeInput: shouldIncludeHistory,
					enrollInResearch: shouldEnrollInResearch,
				});

				trackAIMAUEvent({
					type: 'feedback',
					feedbackResult: 'down',
					proactive: false,
					messageId: agentMessageId,
					messageActions: analyticsMessageActions,
					singleInstrumentationID: agentMessageId,
					attributes: { ...interactionModeExecutedAttributes, ...feedbackTriggerProps },
				});

				sendAnalyticsEvent({
					action: 'submitted',
					actionSubject: 'aupFeedback',
					actionSubjectId: 'incorrectFlagReport',
					attributes: {
						hasAdditionalFeedback: !!data.feedbackText,
						includeInput: shouldIncludeHistory,
						enrollInResearch: shouldEnrollInResearch,
						feedbackVersion: 'v2',
						...feedbackTriggerProps,
					},
				});
			} catch (error) {
				sendAnalyticsEvent({
					action: 'failed',
					actionSubject: 'aupFeedback',
					actionSubjectId: 'incorrectFlagReport',
					attributes: {
						errorMessage: error instanceof Error ? error.message : 'Unknown error',
						feedbackVersion: 'v2',
						...feedbackTriggerProps,
					},
				});
			}
		},
		[
			aaid,
			agentMessageId,
			productKey,
			conversationId,
			history,
			traceId,
			name,
			email,
			trackAIMAUEvent,
			analyticsMessageActions,
			sendAnalyticsEvent,
			showAUPThumbsDownThankYou,
			minionAlias,
			shouldAutoInclude,
			interactionModeExecutedAttributes,
			feedbackTriggerProps,
		],
	);

	const handleIncludeMessagesChecked = useCallback(() => {
		sendAnalyticsEvent({
			action: 'checked',
			actionSubject: 'includeMessagesCheckbox',
			actionSubjectId: 'feedbackConfirmationBanner',
			attributes: {
				feedbackType: feedbackType ?? 'unknown',
				messageId: agentMessageId,
			},
		});
	}, [sendAnalyticsEvent, feedbackType, agentMessageId]);

	const handleOptIn = useCallback(() => {
		setStoreUserMessages?.(true);
		setIsOptedIn(true);
	}, [setStoreUserMessages]);

	return {
		feedbackType,
		isBannerVisible,
		shouldHideCheckboxes: shouldAutoInclude,
		handleThumbClick,
		handleCsatFeedback,
		restartAutoDismiss,
		pauseAutoDismiss: cancelAutoDismiss,
		csatRating,
		handleProvideAdditionalFeedback,
		handleFeedbackSubmit,
		handleDismissBanner,
		handleIncludeMessagesChecked,
		handleOptIn,
		isOptedIn,
		isAUPThumbsDownBannerVisible,
		showAUPThumbsDownBanner: showAUPThumbsDownBannerWithoutTimer,
		hideAUPThumbsDownBanner,
		handleAUPIncorrectFlagSubmit,
	};
};
